import { CalendarSearch } from 'lucide-react';
import { useState } from 'react';
import { prefillFor, validFreeWindow } from '../freeFor.js';

/**
 * 320 (#635, FR-001, FR-008) — "Free for": two date-and-time fields in Malaysia time, prefilled to
 * the viewed day as a whole day (Q1), then Apply. On the line under the toolbar, beside the other
 * view controls (the toolbar keeps its one line, 316 FR-020). An invalid window is refused here,
 * in words, before any request.
 */
export function FreeForControl({
  day,
  from,
  to,
  onApply,
  onClear,
}: {
  /** The viewed day, for the prefill. */
  day: string;
  from: string | null;
  to: string | null;
  onApply: (from: string, to: string) => void;
  onClear: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<{ from: string; to: string }>(() =>
    from && to ? { from, to } : prefillFor(day),
  );
  const [error, setError] = useState<string | null>(null);

  const toggle = () => {
    if (!open) setDraft(from && to ? { from, to } : prefillFor(day));
    setError(null);
    setOpen((o) => !o);
  };
  const apply = () => {
    const problem = validFreeWindow(draft.from, draft.to);
    if (problem) {
      setError(problem);
      return;
    }
    setOpen(false);
    onApply(draft.from, draft.to);
  };

  return (
    <div data-testid="schedule-free-for" className="relative">
      <button
        type="button"
        aria-expanded={open}
        onClick={toggle}
        className={[
          'flex items-center gap-1 whitespace-nowrap rounded border px-2 py-0.5 text-xs',
          from && to
            ? 'border-primary bg-primary text-on-primary'
            : 'border-hairline bg-canvas text-ink',
        ].join(' ')}
      >
        <CalendarSearch aria-hidden="true" size={12} />
        Free for…
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-1 flex w-72 flex-col gap-2 rounded-xl border border-hairline bg-canvas p-3 shadow-xl">
          <label className="flex flex-col gap-1 text-xs text-steel">
            From
            <input
              type="datetime-local"
              aria-label="From"
              value={draft.from}
              onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))}
              className="rounded border border-hairline bg-canvas px-2 py-1 text-sm text-ink"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-steel">
            To
            <input
              type="datetime-local"
              aria-label="To"
              value={draft.to}
              onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))}
              className="rounded border border-hairline bg-canvas px-2 py-1 text-sm text-ink"
            />
          </label>
          <p className="text-[11px] text-steel">
            Malaysia time. Only cars free for the whole window are shown.
          </p>
          {error && (
            <p role="alert" className="text-xs text-semantic-danger-text">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            {from && to && (
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  onClear();
                }}
                className="rounded px-2 py-1 text-xs text-brand-blue hover:underline"
              >
                Remove filter
              </button>
            )}
            <button
              type="button"
              onClick={apply}
              className="rounded bg-primary px-3 py-1 text-xs font-medium text-on-primary"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
